import React, { useState, useEffect, useCallback, useRef } from "react"
import { useParams, useNavigate } from "react-router-dom"
import {
  Row, Col, Card, CardBody, Button, Spinner, Alert,
  FormGroup, Label, Input,
  Modal, ModalHeader, ModalBody, ModalFooter,
} from "reactstrap"
import Switch from "react-switch"
import { connect } from "react-redux"
import { setBreadcrumbItems } from "../../store/actions"
import axiosClient from "../../helpers/axiosClient"
import { uploadToS3 } from "../../helpers/s3Upload"
import "./Empresas.scss"

const Empresa = ({ setBreadcrumbItems, user, empresas: reduxEmpresas }) => {
  document.title = "Empresa | LS-DA - Softlogy"

  const { id } = useParams()
  const navigate = useNavigate()

  const [empresa, setEmpresa] = useState(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [alert, setAlert] = useState({ show: false, message: "", variant: "" })
  const [showCertClave, setShowCertClave] = useState(false)
  const [uploading, setUploading] = useState({})
  const logoInputRefs = [useRef(null), useRef(null), useRef(null), useRef(null), useRef(null)]
  const logoFields = ["logoUrl", "logo2Url", "logo3Url", "logo4Url", "logo5Url"]
  const logoLabels = ["Logo Principal", "Logo 2", "Logo 3", "Logo 4", "Logo 5"]
  const [alianzas, setAlianzas] = useState([])

  // Habilitar modal state
  const [habilitarModal, setHabilitarModal] = useState(false)
  const [habilitarType, setHabilitarType] = useState("") // "facturacion" | "nomina"
  const [habilitarData, setHabilitarData] = useState({ softwareId: "", testSetId: "" })
  const [habilitando, setHabilitando] = useState(false)
  const [habilitarLog, setHabilitarLog] = useState([])
  const logEndRef = useRef(null)

  const addLog = (msg, type = "info") => {
    setHabilitarLog(prev => [...prev, { msg, type, time: new Date().toLocaleTimeString("es-CO") }])
  }

  const openHabilitarModal = (type) => {
    setHabilitarType(type)
    setHabilitarData({
      softwareId: type === "facturacion" ? (empresa?.idSoftware || "") : (empresa?.idSoftwareNomina || ""),
      testSetId: type === "facturacion" ? (empresa?.setIdFactura || "") : (empresa?.setIdNomina || ""),
    })
    setHabilitarLog([])
    setHabilitarModal(true)
  }

  // Build the habilitación XML with empresa NIT and folio
  const buildHabilitarXml = (nit, folio) => {
    const now = new Date()
    const fecha = now.toISOString().slice(0, 10)
    const h = now.toTimeString().slice(0, 8)
    return `<?xml version="1.0" standalone="yes"?>
<Factura xmlns="http://www.w3.org/2001/XMLSchema-instance">
  <Encabezado>
    <llavecomprobante>1</llavecomprobante>
    <nitemisor>${nit}</nitemisor>
    <codSucursal>${nit}</codSucursal>
    <tiporeceptor>2</tiporeceptor>
    <tipoDocRec>13</tipoDocRec>
    <nitreceptor>222222222222</nitreceptor>
    <digitoverificacion>4</digitoverificacion>
    <obligacionesfiscalesreceptor>O-13</obligacionesfiscalesreceptor>
    <tributoreceptor>ZY</tributoreceptor>
    <regimenreceptor>49</regimenreceptor>
    <nombrereceptor>Juan</nombrereceptor>
    <segnombrereceptor/>
    <apellidosreceptor/>
    <nombrecomercialreceptor>Juan</nombrecomercialreceptor>
    <paisreceptor>CO</paisreceptor>
    <codigodepartamento>11</codigodepartamento>
    <departamentoreceptor>Bogota</departamentoreceptor>
    <codigociudadreceptor>11001</codigociudadreceptor>
    <ciudadreceptor>BOGOTA, D.C.</ciudadreceptor>
    <codigopostal>000000</codigopostal>
    <direccionreceptor>Cra 2 62a 20</direccionreceptor>
    <telefonoreceptor>8010523</telefonoreceptor>
    <telefonomovilreceptor/>
    <mailreceptor>jlopez@ls-da.com</mailreceptor>
    <mailreceptorcontacto>jlopez@ls-da.com</mailreceptorcontacto>
    <nombrecontactoreceptor>Juan</nombrecontactoreceptor>
    <tipocomprobante>01</tipocomprobante>
    <noresolucion>18760000001</noresolucion>
    <prefijo>SETP</prefijo>
    <folio>${folio}</folio>
    <fecha>${fecha}</fecha>
    <hora>${h}</hora>
    <moneda>COP</moneda>
    <xslt>1</xslt>
    <subtotal>31008.40</subtotal>
    <metodopago>1</metodopago>
    <mediopago>10;42</mediopago>
    <fechavencimiento>${fecha}</fechavencimiento>
    <terminospago>0</terminospago>
    <baseimpuesto>31008.40</baseimpuesto>
    <totalsindescuento>36900.00</totalsindescuento>
    <totaldescuentos>0.00</totaldescuentos>
    <totalimpuestos>5891.60</totalimpuestos>
    <totalimpuestosretenidos>0.00</totalimpuestosretenidos>
    <total>36900.15</total>
    <redondeo>0.15</redondeo>
    <montoletra>TREINTA Y SEIS MIL NOVECIENTOS PESOS</montoletra>
    <textolibre>Prueba Habilitacion</textolibre>
    <totalcargos>0.00</totalcargos>
    <tipoOpera>10</tipoOpera>
    <totalAnticipo>0.00</totalAnticipo>
    <ncidfact/><nccod/><nciddoc/><ncuuid/><ncfecha/>
    <ndidfact/><ndcod/><ndiddoc/><nduuid/><ndfecha/>
    <extra1/><extra2/><extra3/><extra4/><extra5/><extra6/><extra7/>
    <extra8/><extra9/><extra10/><extra11/><extra12/><extra13/><extra14/><extra15/><extra16/><extra17/><extra18/>
    <idContrato/><tipoContrato/><numMatricula/><numMatriculaMan/>
    <nombreMandatario/><nitDV/><tiposResponsabilidades/>
    <numeroConformidad/><numeroPedido/><ordenCompra/><hojaEntrada/><centroOperacion/>
  </Encabezado>
  <Detalle>
    <Det>
      <llaveComprobante>1</llaveComprobante>
      <idConcepto>1</idConcepto>
      <cantidad>1</cantidad>
      <unidadmedida>94</unidadmedida>
      <impuestolinea>5891.60</impuestolinea>
      <tasa>19.00</tasa>
      <tipo>01</tipo>
      <baseimpuestos>31008</baseimpuestos>
      <UnidadMedidaBase>0.00</UnidadMedidaBase>
      <ValorporUnidad>0.00</ValorporUnidad>
      <subpartidaarancelaria/>
      <identificacionproductos>MEDPIZZ</identificacionproductos>
      <descripcion>MED MASA PIZZA</descripcion>
      <precioUnitario>31008.00</precioUnitario>
      <importe>31008</importe>
      <importemuestra>0.00</importemuestra>
      <tipomuestra/><adicionalInfo/>
      <articulocubierto>0</articulocubierto>
      <DocTercero/><DVTercero/><TipoDocTercero/><redondeo/>
      <extra1/><extra2/><extra3/><extra4/><extra5/><extra6/><extra7/><extra8/><extra9/><extra10/><extra11/><extra12/><extra13/><extra14/><extra15/>
    </Det>
  </Detalle>
  <Impuestos>
    <Imp>
      <llaveComprobante>1</llaveComprobante>
      <idImpuesto>1</idImpuesto>
      <baseimpuestos>31008</baseimpuestos>
      <tasa>19.00</tasa>
      <UnidadMedidaBase>0.00</UnidadMedidaBase>
      <ValorporUnidad>0.00</ValorporUnidad>
      <tipoImpuesto>01</tipoImpuesto>
      <importe>5891.60</importe>
      <redondeo/>
    </Imp>
  </Impuestos>
</Factura>`
  }

  const handleHabilitar = async () => {
    if (!habilitarData.softwareId || !habilitarData.testSetId) {
      setAlert({ show: true, message: "SoftwareId y TestSetId son obligatorios", variant: "warning" })
      return
    }
    setHabilitando(true)
    setHabilitarLog([])

    const MAX_ATTEMPTS = 3
    let folio = 994000001
    let accepted = false
    const API_URL = "https://test.ls-da.com/v2/factura/Habilita"
    const AUTH_TOKEN = process.env.REACT_APP_TOKEN_LSDA

    for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
      addLog(`\u23f3 Enviando petición ${attempt}/${MAX_ATTEMPTS} (folio: ${folio})...`, "loading")

      try {
        const xml = buildHabilitarXml(empresa.nit, folio)
        const base64doc = btoa(unescape(encodeURIComponent(xml)))

        const response = await fetch(API_URL, {
          method: "POST",
          headers: { "Content-Type": "application/json", "Authorization": AUTH_TOKEN },
          body: JSON.stringify({
            usuario: "luda",
            contrasena: "yDQmgqyRK0",
            sucursal: "",
            base64doc,
          }),
        })
        const data = await response.json()

        // Check for error
        if (data.error) {
          addLog(`\u274c Error: ${data.error}`, "error")
        }

        // Check DIAN response
        if (data.DIAN && Array.isArray(data.DIAN) && data.DIAN.length > 0) {
          const dianDesc = data.DIAN[0].Descripcion || ""
          const statusCode = data.DIAN[0].StatusCode || ""
          addLog(`\ud83d\udcdd DIAN: ${dianDesc}`, dianDesc.includes("Aceptado") ? "success" : "warning")

          if (dianDesc.includes(habilitarData.testSetId) && dianDesc.includes("Aceptado")) {
            accepted = true
            addLog(`\u2705 \u00a1Set de prueba ${habilitarData.testSetId} aceptado!`, "success")
            break
          }
        }

        if (data.cufe) {
          addLog(`\ud83d\udd11 CUFE: ${data.cufe.substring(0, 40)}...`, "info")
        }

      } catch (error) {
        addLog(`\u274c Error de conexión: ${error.message}`, "error")
      }

      if (!accepted && attempt < MAX_ATTEMPTS) {
        folio++
        addLog(`\ud83d\udd04 Reintentando con folio ${folio}...`, "loading")
        // Small delay between retries
        await new Promise(r => setTimeout(r, 1500))
      }
    }

    if (accepted) {
      const field = habilitarType === "facturacion" ? "habilitado" : "habilitadoNomina"
      try {
        await axiosClient.put(`empresas/${id}`, {
          [field]: true,
          ...(habilitarType === "facturacion"
            ? { idSoftware: habilitarData.softwareId, setIdFactura: habilitarData.testSetId }
            : { idSoftwareNomina: habilitarData.softwareId, setIdNomina: habilitarData.testSetId }
          ),
        })
        setField(field, true)
        addLog(`\u2705 ${habilitarType === "facturacion" ? "Facturación" : "Nómina"} habilitada y guardada exitosamente`, "success")
      } catch (e) {
        addLog(`\u26a0\ufe0f Habilitado en DIAN pero error al guardar en BD: ${e.message}`, "warning")
      }
    } else {
      addLog(`\u274c No se pudo habilitar después de ${MAX_ATTEMPTS} intentos`, "error")
    }

    setHabilitando(false)
  }

  // Determine if user can manage alianzas
  // SuperAdmin or admin of any alianza empresa
  const canManageAlianzas = user?.esSuperAdmin ||
    (reduxEmpresas || []).some(e => e.esAlianza === true)

  // Find the user's alianza (for non-SuperAdmin)
  const userAlianza = !user?.esSuperAdmin
    ? (reduxEmpresas || []).find(e => e.esAlianza === true)
    : null

  // Dropdowns from XML
  const [paises, setPaises] = useState([])
  const [departamentos, setDepartamentos] = useState([])
  const [ciudades, setCiudades] = useState([])
  const [regimenes, setRegimenes] = useState([])
  const [obligacionesFiscales, setObligacionesFiscales] = useState([])
  const [obligacionesImpuestos, setObligacionesImpuestos] = useState([])
  const [tiposPersona, setTiposPersona] = useState([])

  useEffect(() => {
    const greeting = user
      ? `Bienvenido, ${user.nombre} ${user.apellido}`
      : "Dashboard"
    setBreadcrumbItems(greeting, [
      { title: "LS-DA", link: "#" },
      { title: "Empresas", link: "#" },
      { title: "Actualizar Empresa", link: "#" },
    ])
  }, [setBreadcrumbItems, user])

  // Fetch empresa by ID
  const fetchEmpresa = useCallback(async () => {
    setLoading(true)
    try {
      const response = await axiosClient.get(`empresas/${id}`)
      const data = response.data.data || response.data
      setEmpresa(data)
    } catch (error) {
      setAlert({ show: true, message: "Error al obtener la empresa", variant: "danger" })
    } finally {
      setLoading(false)
    }
  }, [id])

  const fetchXmlList = async (url) => {
    const response = await fetch(url)
    if (!response.ok) {
      console.error(`[fetchXmlList] HTTP ${response.status} for ${url}`)
      return []
    }
    const contentType = response.headers.get("content-type") || ""
    const xmlText = await response.text()
    // Detect SPA redirect: Amplify returns index.html instead of XML
    if (contentType.includes("text/html") || xmlText.trimStart().startsWith("<!doctype") || xmlText.trimStart().startsWith("<!DOCTYPE")) {
      console.error(`[fetchXmlList] Got HTML instead of XML for ${url} — check Amplify rewrite rules`)
      return []
    }
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlText, "application/xml")
    const parseError = xmlDoc.querySelector("parsererror")
    if (parseError) {
      console.error(`[fetchXmlList] XML parse error for ${url}:`, parseError.textContent)
      return []
    }
    const rows = xmlDoc.getElementsByTagName("Row")
    const items = []
    for (let row of rows) {
      const code = row.querySelector('[ColumnRef="code"] SimpleValue')
      const name = row.querySelector('[ColumnRef="name"] SimpleValue')
      if (code && name) items.push({ code: code.textContent, name: name.textContent })
    }
    return items
  }

  const fetchDropdowns = async () => {
    try { setPaises(await fetchXmlList("/listas/Paises-2.1.xml")) } catch (e) { }
    try { setDepartamentos(await fetchXmlList("/listas/Departamentos-2.1.xml")) } catch (e) { }
    try { setRegimenes(await fetchXmlList("/listas/TipoRegimen-2.1.xml")) } catch (e) { }
    try { setObligacionesFiscales(await fetchXmlList("/listas/TipoResponsabilidad-2.1.xml")) } catch (e) { }
    try { setObligacionesImpuestos(await fetchXmlList("/listas/ObligacionesImpuestos-2.1.xml")) } catch (e) { }
    try { setTiposPersona(await fetchXmlList("/listas/TipoOrganizacion-2.1.xml")) } catch (e) { }
  }

  // Fetch ciudades filtered by departamento code
  const fetchCiudades = async (dep) => {
    if (!dep) return
    try {
      const all = await fetchXmlList("/listas/Municipio-2.1.xml")
      setCiudades(all.filter(c => c.code.startsWith(dep)))
    } catch (e) { /* ignore */ }
  }

  useEffect(() => { fetchEmpresa(); fetchDropdowns(); fetchAlianzas() }, [fetchEmpresa])
  useEffect(() => { if (empresa?.codDepartamento) fetchCiudades(empresa.codDepartamento) }, [empresa?.codDepartamento])

  // Fetch alianzas
  const fetchAlianzas = async () => {
    try {
      const response = await axiosClient.get("empresas/alianzas")
      const data = response.data.data || response.data
      setAlianzas(Array.isArray(data) ? data : [])
    } catch (e) { /* ignore - user may not have permission */ }
  }

  // Update field
  const setField = (field, value) => {
    setEmpresa(prev => ({ ...prev, [field]: value }))
  }

  // Save
  const handleSubmit = async () => {
    if (!empresa.tipoPersona) {
      setAlert({ show: true, message: "Tipo de Persona es obligatorio", variant: "danger" })
      return
    }
    setSaving(true)
    try {
      await axiosClient.put(`empresas/${id}`, {
        nombre: empresa.nombre,
        nit: empresa.nit,
        digVerificacion: empresa.digVerificacion,
        email: empresa.email,
        telefono: empresa.telefono,
        direccion: empresa.direccion,
        logoUrl: empresa.logoUrl,
        logo2Url: empresa.logo2Url,
        logo3Url: empresa.logo3Url,
        logo4Url: empresa.logo4Url,
        logo5Url: empresa.logo5Url,
        activo: empresa.activo,
        pais: empresa.pais,
        departamento: empresa.departamento,
        ciudad: empresa.ciudad,
        municipio: empresa.ciudad || empresa.municipio,
        codDepartamento: empresa.codDepartamento,
        codCiudad: empresa.codCiudad,
        regimen: empresa.regimen,
        obFiscales: empresa.obFiscales,
        obImpuestos: empresa.obImpuestos,
        tipoOperacion: "10",
        leyenda: empresa.leyenda,
        pinSoftware: "70963",
        idSoftware: empresa.idSoftware,
        certificadoRuta: `/home/Colombia/Certificados/${empresa.nit}/${empresa.nit}.pfx`,
        certificadoClave: empresa.certificadoClave,
        proveedorTecnologico: "Supplier",
        esAlianza: empresa.esAlianza,
        empresaPadreId: empresa.empresaPadreId,
        sqlServerId: empresa.sqlServerId,
        emailContacto: empresa.emailContacto,
        tipoPdf: empresa.tipoPdf,
        idSoftwareNomina: empresa.idSoftwareNomina,
        setIdNomina: empresa.setIdNomina,
        setIdFactura: empresa.setIdFactura,
        habilitado: empresa.habilitado,
        habilitadonomina: empresa.habilitadonomina,
        tipoPersona: empresa.tipoPersona,
        notasPdf: empresa.notasPdf,
      })
      setAlert({ show: true, message: "Empresa actualizada exitosamente", variant: "success" })
      setTimeout(() => setAlert({ show: false }), 4000)
    } catch (error) {
      const msg = error.response?.data?.message || "Error al actualizar la empresa"
      setAlert({ show: true, message: typeof msg === "string" ? msg : JSON.stringify(msg), variant: "danger" })
    } finally {
      setSaving(false)
    }
  }

  const inputStyle = { borderRadius: 8, border: "1px solid #e2e8f0", padding: "8px 12px", fontSize: "0.9rem" }
  const labelStyle = { fontWeight: 600, fontSize: "0.85rem", color: "#334155" }
  const sectionTitle = { fontWeight: 700, color: "#1e293b", marginBottom: 16, marginTop: 8, display: "flex", alignItems: "center", gap: 8 }

  return (
    <React.Fragment>
      <Row>
        <Col className="col-12">
          <Card style={{ border: "none", borderRadius: 16, boxShadow: "0 4px 24px rgba(0,0,0,0.06)" }}>
            <CardBody style={{ padding: "2rem" }}>
              {/* Header */}
              <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                <div className="d-flex align-items-center gap-3">
                  {empresa?.logoUrl ? (
                    <img src={empresa.logoUrl} alt="" style={{ width: 48, height: 48, objectFit: "contain", borderRadius: 10, border: "1px solid #e2e8f0" }}
                      onError={e => { e.target.style.display = "none" }} />
                  ) : (
                    <div style={{ width: 48, height: 48, borderRadius: 10, backgroundColor: "#556ee6", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 20 }}>
                      {empresa?.nombre?.charAt(0)?.toUpperCase() || "E"}
                    </div>
                  )}
                  <div>
                    <h4 className="mb-0" style={{ fontWeight: 700 }}>{empresa?.nombre || "Empresa"}</h4>
                    <small className="text-muted">NIT: {empresa?.nit}{empresa?.digVerificacion ? `-${empresa.digVerificacion}` : ""}</small>
                  </div>
                </div>
                <div className="d-flex gap-2">
                  <Button color="primary" onClick={handleSubmit} disabled={saving || loading} style={{ borderRadius: 8, minWidth: 160 }}>
                    {saving ? <><Spinner size="sm" className="me-1" />Guardando...</> : <><i className="mdi mdi-content-save me-1" />Guardar Cambios</>}
                  </Button>
                  <Button color="outline-secondary" onClick={() => navigate("/portal/consultar-empresas")} style={{ borderRadius: 8 }}>
                    <i className="mdi mdi-arrow-left me-1" />Volver
                  </Button>
                </div>
              </div>

              {/* Alert */}
              {alert.show && (
                <Alert color={alert.variant} className="mb-3" toggle={() => setAlert({ show: false })}>
                  <i className={`mdi ${alert.variant === "success" ? "mdi-check-circle" : "mdi-alert-circle"} me-1`} />
                  {alert.message}
                </Alert>
              )}

              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" />
                  <p className="mt-2 text-muted">Cargando empresa...</p>
                </div>
              ) : empresa ? (
                <>
                  {/* ── Información General ── */}
                  <h5 style={sectionTitle}><i className="mdi mdi-information-outline" style={{ color: "#556ee6" }} />Información General</h5>
                  <Row className="mb-3">
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>Nombre</Label>
                        <Input type="text" style={inputStyle} value={empresa.nombre || ""} onChange={e => setField("nombre", e.target.value)} />
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup>
                        <Label style={labelStyle}>NIT</Label>
                        <Input type="text" style={{ ...inputStyle, backgroundColor: "#f1f5f9" }} value={empresa.nit || ""} disabled />
                      </FormGroup>
                    </Col>
                    <Col md={1}>
                      <FormGroup>
                        <Label style={labelStyle}>DV</Label>
                        <Input type="text" style={inputStyle} value={empresa.digVerificacion || ""} onChange={e => setField("digVerificacion", e.target.value)} />
                      </FormGroup>
                    </Col>
                    <Col md={1}>
                      <FormGroup>
                        <Label style={labelStyle}>Código</Label>
                        <Input type="text" style={{ ...inputStyle, backgroundColor: "#f1f5f9" }} value={empresa.codigo || ""} disabled />
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup>
                        <Label style={labelStyle}>Estado</Label>
                        <div className="d-flex align-items-center gap-2 mt-1">
                          <Switch onColor="#02a499" offColor="#dc3545" onChange={() => setField("activo", !empresa.activo)} checked={empresa.activo || false} height={22} width={44} />
                          <span style={{ fontWeight: 600, fontSize: "0.85rem", color: empresa.activo ? "#059669" : "#dc3545" }}>
                            {empresa.activo ? "Activo" : "Inactivo"}
                          </span>
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup>
                        <Label style={labelStyle}>Es Alianza</Label>
                        <div className="d-flex align-items-center gap-2 mt-1">
                          <Switch onColor="#556ee6" onChange={() => setField("esAlianza", !empresa.esAlianza)} checked={empresa.esAlianza || false} height={22} width={44}
                            disabled={!user?.esSuperAdmin} />
                          <span style={{ fontWeight: 600, fontSize: "0.85rem" }}>{empresa.esAlianza ? "Sí" : "No"}</span>
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Alianza selector */}
                  {!empresa.esAlianza && (
                    <Row className="mb-3">
                      <Col md={4}>
                        <FormGroup>
                          <Label style={labelStyle}>
                            <i className="mdi mdi-link-variant me-1" />
                            Alianza (Empresa Padre)
                          </Label>
                          {user?.esSuperAdmin ? (
                            <Input type="select" style={inputStyle} value={empresa.empresaPadreId || ""}
                              onChange={e => setField("empresaPadreId", e.target.value || null)}>
                              <option value="">Sin alianza</option>
                              {alianzas.map(a => (
                                <option key={a.id} value={a.id}>{a.nombre} (NIT: {a.nit})</option>
                              ))}
                            </Input>
                          ) : (
                            <Input type="text" style={{ ...inputStyle, backgroundColor: "#f1f5f9" }}
                              value={userAlianza?.nombre || alianzas.find(a => a.id === empresa.empresaPadreId)?.nombre || empresa.empresaPadreId || "Sin alianza"}
                              disabled />
                          )}
                        </FormGroup>
                      </Col>
                    </Row>
                  )}

                  <hr />

                  {/* ── Contacto ── */}
                  <h5 style={sectionTitle}><i className="mdi mdi-email-outline" style={{ color: "#556ee6" }} />Contacto</h5>
                  <Row className="mb-3">
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>Email</Label>
                        <Input type="email" style={inputStyle} value={empresa.email || ""} onChange={e => setField("email", e.target.value)} />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>Email Contacto</Label>
                        <Input type="email" style={inputStyle} value={empresa.emailContacto || ""} onChange={e => setField("emailContacto", e.target.value)} placeholder="contacto@empresa.com" />
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup>
                        <Label style={labelStyle}>Teléfono</Label>
                        <Input type="text" style={inputStyle} value={empresa.telefono || ""} onChange={e => setField("telefono", e.target.value)} />
                      </FormGroup>
                    </Col>
                    <Col md={2}>
                      <FormGroup>
                        <Label style={labelStyle}>Tipo PDF</Label>
                        <Input type="select" style={inputStyle} value={empresa.tipoPdf || ""} onChange={e => setField("tipoPdf", e.target.value)}>
                          <option value="">Seleccione</option>
                          <option value="V1">V1</option>
                          <option value="V2">V2</option>
                          <option value="V3">V3</option>
                          <option value="V4">V4</option>
                          <option value="V5">V5</option>
                          <option value="V6">V6</option>
                          <option value="V7">V7</option>
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row className="mb-3">
                    <Col md={5}>
                      <FormGroup>
                        <Label style={labelStyle}>Dirección</Label>
                        <Input type="text" style={inputStyle} value={empresa.direccion || ""} onChange={e => setField("direccion", e.target.value)} />
                      </FormGroup>
                    </Col>
                  </Row>

                  <hr />

                  {/* ── Ubicación ── */}
                  <h5 style={sectionTitle}><i className="mdi mdi-map-marker-outline" style={{ color: "#556ee6" }} />Ubicación</h5>
                  <Row className="mb-3">
                    <Col md={2}>
                      <FormGroup>
                        <Label style={labelStyle}>País</Label>
                        <Input type="select" style={inputStyle} value={empresa.pais || ""}
                          onChange={e => setField("pais", e.target.value)}>
                          <option value="">Seleccione</option>
                          {paises.map(p => <option key={p.code} value={p.code}>{p.name}</option>)}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label style={labelStyle}>Departamento</Label>
                        <Input type="select" style={inputStyle} value={empresa.codDepartamento || ""}
                          onChange={e => {
                            const dep = departamentos.find(d => d.code === e.target.value)
                            setEmpresa(prev => ({ ...prev, codDepartamento: e.target.value, departamento: dep?.name || e.target.value, codCiudad: "", ciudad: "", municipio: "" }))
                          }}>
                          <option value="">Seleccione</option>
                          {departamentos.map(d => <option key={d.code} value={d.code}>{d.name}</option>)}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label style={labelStyle}>Ciudad</Label>
                        <Input type="select" style={inputStyle} value={empresa.codCiudad || ""}
                          onChange={e => {
                            const city = ciudades.find(c => c.code === e.target.value)
                            setEmpresa(prev => ({ ...prev, codCiudad: e.target.value, ciudad: city?.name || e.target.value }))
                          }}>
                          <option value="">Seleccione</option>
                          {ciudades.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  <hr />

                  {/* ── Datos Fiscales / DIAN ── */}
                  <h5 style={sectionTitle}><i className="mdi mdi-file-document-outline" style={{ color: "#556ee6" }} />Datos Fiscales / DIAN</h5>
                  <Row className="mb-3">
                    <Col md={3}>
                      <FormGroup>
                        <Label style={labelStyle}>Régimen</Label>
                        <Input type="select" style={inputStyle} value={empresa.regimen || ""}
                          onChange={e => setField("regimen", e.target.value)}>
                          <option value="">Seleccione</option>
                          {regimenes.map(r => <option key={r.code} value={r.code}>{r.name}</option>)}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label style={labelStyle}>Obligaciones Fiscales</Label>
                        <Input type="select" style={inputStyle} value={empresa.obFiscales || ""}
                          onChange={e => setField("obFiscales", e.target.value)}>
                          <option value="">Seleccione</option>
                          {obligacionesFiscales.map(o => <option key={o.code} value={o.code}>{o.name}</option>)}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label style={labelStyle}>Obligaciones Impuestos</Label>
                        <Input type="select" style={inputStyle} value={empresa.obImpuestos || ""}
                          onChange={e => setField("obImpuestos", e.target.value)}>
                          <option value="">Seleccione</option>
                          {obligacionesImpuestos.map(o => <option key={o.code} value={o.code}>{o.name}</option>)}
                        </Input>
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label style={labelStyle}>Tipo Persona</Label>
                        <Input type="select" style={inputStyle} value={empresa.tipoPersona || ""}
                          onChange={e => setField("tipoPersona", e.target.value)}>
                          <option value="">Seleccione</option>
                          {tiposPersona.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>

                  <hr />

                  {/* ── Parámetros de Empresa ── */}
                  <h5 style={sectionTitle}><i className="mdi mdi-tune-vertical" style={{ color: "#556ee6" }} />Parámetros de Empresa</h5>
                  <Row className="mb-3">
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>Leyenda</Label>
                        <Input type="text" style={inputStyle} value={empresa.leyenda || ""} onChange={e => setField("leyenda", e.target.value)} />
                      </FormGroup>
                    </Col>
                    <Col md={8}>
                      <FormGroup>
                        <Label style={labelStyle}>Notas PDF</Label>
                        <Input type="textarea" style={{ ...inputStyle, minHeight: 80, resize: "vertical" }}
                          value={empresa.notasPdf || ""} maxLength={250}
                          onChange={e => setField("notasPdf", e.target.value)}
                          placeholder="Notas que aparecerán en el PDF" />
                        <small style={{ color: "#94a3b8", fontSize: 11 }}>{(empresa.notasPdf || "").length}/250 caracteres</small>
                      </FormGroup>
                    </Col>
                  </Row>

                  <hr />

                  {/* ── Software & Certificado ── */}
                  <h5 style={sectionTitle}><i className="mdi mdi-cog-outline" style={{ color: "#556ee6" }} />Software & Certificado</h5>
              <Row className="mb-3">
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>ID Software Facturación</Label>
                        <Input type="text" style={inputStyle} value={empresa.idSoftware || ""} onChange={e => setField("idSoftware", e.target.value)} />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>Set ID Factura</Label>
                        <Input type="text" style={inputStyle} value={empresa.setIdFactura || ""} onChange={e => setField("setIdFactura", e.target.value)} />
                      </FormGroup>
                    </Col>
                    {!empresa.habilitado && (
                      <Col md={4} className="d-flex align-items-end pb-3">
                        <Button color="warning" size="sm" onClick={() => openHabilitarModal("facturacion")} style={{ borderRadius: 8, fontWeight: 600 }}>
                          <i className="mdi mdi-check-decagram me-1" />Habilitar Facturación
                        </Button>
                      </Col>
                    )}
                  </Row>
                  <Row className="mb-3">
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>ID Software Nómina</Label>
                        <Input type="text" style={inputStyle} value={empresa.idSoftwareNomina || ""} onChange={e => setField("idSoftwareNomina", e.target.value)} />
                      </FormGroup>
                    </Col>
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>Set ID Nómina</Label>
                        <Input type="text" style={inputStyle} value={empresa.setIdNomina || ""} onChange={e => setField("setIdNomina", e.target.value)} />
                      </FormGroup>
                    </Col>
                    {!empresa.habilitadoNomina && (
                      <Col md={4} className="d-flex align-items-end pb-3">
                        <Button color="warning" size="sm" onClick={() => openHabilitarModal("nomina")} style={{ borderRadius: 8, fontWeight: 600 }}>
                          <i className="mdi mdi-check-decagram me-1" />Habilitar Nómina
                        </Button>
                      </Col>
                    )}
                  </Row>
                  <Row className="mb-3">
                    <Col md={3}>
                      <FormGroup>
                        <Label style={labelStyle}>Certificado Clave</Label>
                        <div className="d-flex align-items-center gap-2">
                          <Input type={showCertClave ? "text" : "password"} style={inputStyle} value={empresa.certificadoClave || ""}
                            onChange={e => setField("certificadoClave", e.target.value)} />
                          <Button outline color="secondary" size="sm" onClick={() => setShowCertClave(!showCertClave)} style={{ borderRadius: 6, whiteSpace: "nowrap" }}>
                            {showCertClave ? "Ocultar" : "Mostrar"}
                          </Button>
                        </div>
                      </FormGroup>
                    </Col>
                    <Col md={12}>
                      <FormGroup>
                        <Label style={labelStyle}><i className="mdi mdi-image-multiple" style={{ color: "#556ee6" }} /> Logos de la Empresa (hasta 5)</Label>
                        <div style={{ fontSize: 11, color: "#6c757d", marginTop: 2, marginBottom: 10, background: "#f8f9fa", borderRadius: 6, padding: "6px 12px", border: "1px solid #e9ecef" }}>
                          <i className="mdi mdi-information-outline me-1" style={{ color: "#556ee6" }} />
                          <strong>Requisitos:</strong> Formato PNG, JPG o WEBP · Peso máximo 1 MB · Tamaño máximo 500 × 500 píxeles
                        </div>
                        <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
                          {logoFields.map((field, idx) => (
                            <div key={field} style={{
                              width: 140, minHeight: 140, border: empresa[field] ? "2px solid #34c38f" : "2px dashed #dee2e6",
                              borderRadius: 10, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                              background: empresa[field] ? "#f0fdf4" : "#f8fafc", padding: 8, position: "relative", transition: "all 0.2s",
                            }}>
                              <div style={{ fontSize: 10, fontWeight: 600, color: "#6c757d", marginBottom: 4 }}>{logoLabels[idx]}</div>
                              {empresa[field] ? (
                                <>
                                  <img src={empresa[field]} alt={logoLabels[idx]} style={{ maxHeight: 60, maxWidth: "100%", objectFit: "contain", borderRadius: 4 }} />
                                  <button type="button" onClick={() => setField(field, "")}
                                    style={{ position: "absolute", top: 4, right: 4, background: "#dc3545", color: "#fff", border: "none", borderRadius: "50%", width: 20, height: 20, fontSize: 11, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", lineHeight: 1 }}
                                    title="Eliminar logo">×</button>
                                </>
                              ) : (
                                <>
                                  <i className="mdi mdi-image-plus" style={{ fontSize: 28, color: "#adb5bd" }} />
                                  <input type="file" accept=".png,.jpg,.jpeg,.webp,.svg" ref={logoInputRefs[idx]} style={{ display: "none" }}
                                    onChange={async e => {
                                      const file = e.target.files?.[0]
                                      if (!file) return
                                      // Validar formato
                                      const allowedTypes = ["image/png", "image/jpeg", "image/jpg", "image/webp", "image/svg+xml"]
                                      if (!allowedTypes.includes(file.type)) {
                                        setAlert({ show: true, message: `Formato no permitido. Use PNG, JPG o WEBP.`, variant: "danger" })
                                        e.target.value = null; return
                                      }
                                      // Validar peso (1 MB)
                                      if (file.size > 1 * 1024 * 1024) {
                                        setAlert({ show: true, message: `El archivo pesa ${(file.size / 1024 / 1024).toFixed(1)} MB. El máximo es 1 MB.`, variant: "danger" })
                                        e.target.value = null; return
                                      }
                                      // Validar dimensiones (500x500)
                                      if (file.type !== "image/svg+xml") {
                                        const valid = await new Promise(resolve => {
                                          const img = new Image()
                                          img.onload = () => resolve(img.width <= 500 && img.height <= 500)
                                          img.onerror = () => resolve(false)
                                          img.src = URL.createObjectURL(file)
                                        })
                                        if (!valid) {
                                          setAlert({ show: true, message: `La imagen excede 500×500 píxeles. Reduzca el tamaño antes de subirla.`, variant: "danger" })
                                          e.target.value = null; return
                                        }
                                      }
                                      setUploading(prev => ({ ...prev, [field]: true }))
                                      try {
                                        const suffix = idx > 0 ? `_logo${idx + 1}` : ""
                                        const url = await uploadToS3(file, "logos", `${empresa.nit || empresa.codigo || Date.now()}${suffix}`)
                                        setField(field, url)
                                      } catch (err) {
                                        setAlert({ show: true, message: `Error al subir ${logoLabels[idx]}: ` + err.message, variant: "danger" })
                                      } finally {
                                        setUploading(prev => ({ ...prev, [field]: false }))
                                        e.target.value = null
                                      }
                                    }} />
                                  <Button color="primary" size="sm" disabled={uploading[field]}
                                    onClick={() => logoInputRefs[idx].current?.click()}
                                    style={{ borderRadius: 6, fontSize: 10, padding: "2px 8px", marginTop: 4 }}>
                                    {uploading[field] ? <><Spinner size="sm" style={{ width: 12, height: 12 }} /> Subiendo...</> : "Subir"}
                                  </Button>
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </FormGroup>
                    </Col>
                  </Row>

                  <hr />

                  {/* ── Datos Internos ── */}
                  <h5 style={sectionTitle}><i className="mdi mdi-database-outline" style={{ color: "#556ee6" }} />Datos Internos</h5>
                  <Row className="mb-3">
                    <Col md={4}>
                      <FormGroup>
                        <Label style={labelStyle}>Empresa Padre ID</Label>
                        <Input type="text" style={inputStyle} value={empresa.empresaPadreId || ""} onChange={e => setField("empresaPadreId", e.target.value || null)} />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label style={{ ...labelStyle, color: "#94a3b8" }}>Creado</Label>
                        <Input type="text" style={{ ...inputStyle, backgroundColor: "#f1f5f9" }}
                          value={empresa.createdAt ? new Date(empresa.createdAt).toLocaleString("es-CO") : "—"} disabled />
                      </FormGroup>
                    </Col>
                    <Col md={3}>
                      <FormGroup>
                        <Label style={{ ...labelStyle, color: "#94a3b8" }}>Última Actualización</Label>
                        <Input type="text" style={{ ...inputStyle, backgroundColor: "#f1f5f9" }}
                          value={empresa.updatedAt ? new Date(empresa.updatedAt).toLocaleString("es-CO") : "—"} disabled />
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* ── Estadísticas ── */}
                  {empresa._count && (
                    <>
                      <hr />
                      <h5 style={sectionTitle}><i className="mdi mdi-chart-bar" style={{ color: "#556ee6" }} />Estadísticas</h5>
                      <div className="d-flex gap-3 flex-wrap mb-3">
                        {[
                          { label: "Sucursales", value: empresa._count.sucursales, icon: "mdi-store", color: "#556ee6" },
                          { label: "Resoluciones", value: empresa._count.resoluciones, icon: "mdi-file-document-outline", color: "#02a499" },
                          { label: "Usuarios", value: empresa._count.usuariosEmpresa, icon: "mdi-account-group-outline", color: "#f59e0b" },
                        ].map(stat => (
                          <div key={stat.label} style={{
                            padding: "12px 20px", borderRadius: 10, border: "1px solid #e2e8f0",
                            backgroundColor: "#fafbfc", minWidth: 140, textAlign: "center"
                          }}>
                            <i className={`mdi ${stat.icon}`} style={{ fontSize: 24, color: stat.color }} />
                            <div style={{ fontSize: "1.3rem", fontWeight: 700, color: "#1e293b" }}>{stat.value ?? 0}</div>
                            <small className="text-muted">{stat.label}</small>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="text-center py-5">
                  <i className="mdi mdi-alert-circle-outline" style={{ fontSize: 48, color: "#dc3545" }} />
                  <p className="text-muted mt-2">No se pudo cargar la empresa</p>
                  <Button color="primary" size="sm" onClick={fetchEmpresa}>Reintentar</Button>
                </div>
              )}
            </CardBody>
          </Card>
        </Col>
      </Row>

      {/* ── Modal Habilitar ── */}
      <Modal isOpen={habilitarModal} toggle={() => !habilitando && setHabilitarModal(false)} size="lg">
        <ModalHeader toggle={() => !habilitando && setHabilitarModal(false)}>
          <i className="mdi mdi-check-decagram me-2" style={{ color: "#f59e0b" }} />
          Habilitar {habilitarType === "facturacion" ? "Facturación" : "Nómina"}
        </ModalHeader>
        <ModalBody>
          <Row>
            <Col md={6}>
              <FormGroup>
                <Label style={labelStyle}>Software ID</Label>
                <Input style={inputStyle} value={habilitarData.softwareId} disabled={habilitando}
                  onChange={e => setHabilitarData(p => ({ ...p, softwareId: e.target.value }))}
                  placeholder="ID del software" />
              </FormGroup>
            </Col>
            <Col md={6}>
              <FormGroup>
                <Label style={labelStyle}>TestSetId</Label>
                <Input style={inputStyle} value={habilitarData.testSetId} disabled={habilitando}
                  onChange={e => setHabilitarData(p => ({ ...p, testSetId: e.target.value }))}
                  placeholder="Test Set ID para habilitar" />
              </FormGroup>
            </Col>
          </Row>

          {/* Log de progreso */}
          {habilitarLog.length > 0 && (
            <div style={{
              background: "#0f172a", borderRadius: 10, padding: "12px 16px",
              maxHeight: 280, overflowY: "auto", marginTop: 12,
              fontFamily: "'Courier New', monospace", fontSize: 12.5,
            }}>
              {habilitarLog.map((entry, i) => (
                <div key={i} style={{
                  padding: "4px 0", borderBottom: i < habilitarLog.length - 1 ? "1px solid #1e293b" : "none",
                  color: entry.type === "error" ? "#f87171"
                    : entry.type === "success" ? "#4ade80"
                    : entry.type === "warning" ? "#fbbf24"
                    : entry.type === "loading" ? "#60a5fa" : "#e2e8f0",
                }}>
                  <span style={{ color: "#64748b", marginRight: 8 }}>[{entry.time}]</span>
                  {entry.msg}
                </div>
              ))}
              <div ref={logEndRef} />
            </div>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" outline onClick={() => setHabilitarModal(false)} disabled={habilitando}
            style={{ borderRadius: 8 }}>
            {habilitarLog.length > 0 && !habilitando ? "Cerrar" : "Cancelar"}
          </Button>
          {!habilitando && habilitarLog.length === 0 && (
            <Button color="warning" onClick={handleHabilitar}
              style={{ borderRadius: 8, fontWeight: 600 }}>
              <i className="mdi mdi-check-decagram me-1" />Habilitar
            </Button>
          )}
          {habilitando && (
            <Button color="warning" disabled style={{ borderRadius: 8, fontWeight: 600 }}>
              <Spinner size="sm" className="me-1" />Procesando...
            </Button>
          )}
        </ModalFooter>
      </Modal>
    </React.Fragment>
  )
}

const mapStateToProps = state => ({
  user: state.Login.user,
  empresas: state.Login.empresas,
})

export default connect(mapStateToProps, { setBreadcrumbItems })(Empresa)